import { useRef } from 'react'
import type { Mode } from './viewModel'

/**
 * The arcade's SECOND level of navigation.
 *
 * WHY IT EXISTS. Every view was one page that scrolled forever. Big Spenders
 * alone stacked eight KPI tiles, a cumulative race, a platform trend, a
 * thirteen-column board, two group charts and a ranked ladder into a single
 * column, so the thing a viewer opens the board for — where do I sit, and who
 * is at the top — was above six panels they had to scroll past to reach
 * anything else, and every one of those panels re-rendered on every window
 * change whether or not it was on screen.
 *
 * The top-level modes (Big Spenders / Token Titans / … ) are unchanged: they
 * are the board's identity and people navigate by them. This is a tab strip
 * INSIDE one of them, and it holds two rules:
 *
 *  1. THE FIRST TAB IS THE LANDING CONTENT AND IT IS ALWAYS THE SUMMARY. A
 *     view opens on the numbers and the leaderboard; everything analytical is
 *     one click away and nothing is buried under a scroll.
 *  2. THE TAB IS IN THE URL. `?view=spend&tab=race` is a link to a panel, not
 *     to a page with a panel somewhere on it. It is written with
 *     `replaceState`, matching how the profile drill-down already syncs `?u=`
 *     — the board has one history entry and switching a tab is not navigation.
 *
 * ACCESSIBILITY. A real `tablist`/`tab`/`tabpanel` triple with roving
 * tabindex: Left/Right move, Home/End jump, and the strip is one tab stop
 * rather than seven. Activation follows selection because every panel here is
 * already in memory — the model is built once and the panels only read it — so
 * there is no cost to arrowing through them.
 */

export interface SubTab {
  readonly id: string
  readonly label: string
}

/**
 * The sub-tabs each top-level mode offers, in order. The FIRST is the landing
 * content, and `defaultTab` depends on that.
 *
 * Two modes are absent on purpose. `tokens` and `mains` are a single ranked
 * ladder each — one skeleton, one screen — and a tab strip over one panel is
 * chrome. The rule is that a view earns tabs when it runs long, not because
 * its neighbours have them.
 *
 * `teams` gained a strip on 31 Aug 2026 when the race track landed beside the
 * grid. The grid stays the landing content: it is what the mode has always
 * been, and the track is a second reading of the same numbers rather than a
 * replacement for them.
 */
export const VIEW_TABS: Partial<Record<Mode, readonly SubTab[]>> = {
  spend: [
    { id: 'summary', label: 'Summary' },
    { id: 'heatmap', label: 'Activity heatmap' },
    { id: 'race', label: 'The race' },
    { id: 'trend', label: 'Platform trend' },
    { id: 'board', label: 'The board' },
    { id: 'groups', label: 'Groups' },
  ],
  teams: [
    { id: 'summary', label: 'The grid' },
    { id: 'race', label: 'Race track' },
  ],
  rag: [
    { id: 'summary', label: 'Summary' },
    { id: 'corpora', label: 'The corpora' },
    { id: 'searches', label: 'Who searches' },
    { id: 'method', label: 'Where this comes from' },
  ],
  usage: [
    { id: 'summary', label: 'Summary' },
    { id: 'code', label: 'Code interpreter' },
    { id: 'builtins', label: 'Built-ins' },
    { id: 'servers', label: 'MCP servers' },
    { id: 'tools', label: 'Every tool' },
    { id: 'governance', label: 'Denials & latency' },
    { id: 'method', label: 'Where this comes from' },
  ],
  taxonomy: [
    { id: 'summary', label: 'Summary' },
    { id: 'trend', label: 'Over time' },
    { id: 'table', label: 'Every topic' },
    { id: 'method', label: 'How labelling works' },
  ],
  rivalry: [
    { id: 'summary', label: 'The league' },
    { id: 'select', label: 'Who is playing' },
    { id: 'weeks', label: 'By week' },
    { id: 'h2h', label: 'Head to head' },
  ],
  buildlog: [
    { id: 'summary', label: 'Recent' },
    { id: 'timeline', label: 'The whole build' },
    { id: 'method', label: 'How this list is made' },
  ],
}

/** The landing tab for a mode, or null where the mode has no tabs at all. */
export function defaultTab(mode: Mode): string | null {
  return VIEW_TABS[mode]?.[0]?.id ?? null
}

/**
 * Resolves a tab id against a mode, falling back to that mode's landing tab.
 *
 * Every entry point goes through this — the URL on first load, a mode switch,
 * and the click handler — so a stale `?tab=` from a link to a different view
 * lands on the summary instead of rendering nothing at all. A blank view is
 * how a shared link quietly stops working.
 */
export function resolveTab(mode: Mode, candidate: string | null | undefined): string | null {
  const tabs = VIEW_TABS[mode]
  if (!tabs || tabs.length === 0) return null
  return tabs.some((t) => t.id === candidate) ? candidate! : tabs[0]!.id
}

export function SubTabs({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: readonly SubTab[]
  active: string
  onSelect: (id: string) => void
  label: string
}) {
  const strip = useRef<HTMLDivElement | null>(null)

  function move(delta: number | 'first' | 'last') {
    const at = tabs.findIndex((t) => t.id === active)
    const next =
      delta === 'first'
        ? 0
        : delta === 'last'
          ? tabs.length - 1
          : (at + delta + tabs.length) % tabs.length
    const target = tabs[next]
    if (!target) return
    onSelect(target.id)
    // Focus follows selection so the arrow keys keep working from the new
    // position; without this the roving tabindex moves out from under the
    // focused element and the next arrow press does nothing.
    strip.current?.querySelector<HTMLButtonElement>(`[data-tabid="${CSS.escape(target.id)}"]`)?.focus()
  }

  return (
    <div className="subtabs" role="tablist" aria-label={label} ref={strip}>
      {tabs.map((t) => {
        const on = t.id === active
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`subtab-${t.id}`}
            data-tabid={t.id}
            aria-selected={on}
            aria-controls={`subpanel-${t.id}`}
            tabIndex={on ? 0 : -1}
            data-on={on ? '1' : '0'}
            onClick={() => onSelect(t.id)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault()
                move(1)
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault()
                move(-1)
              } else if (e.key === 'Home') {
                e.preventDefault()
                move('first')
              } else if (e.key === 'End') {
                e.preventDefault()
                move('last')
              }
            }}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

/** The panel half of the triple. Rendered for the ACTIVE tab only: the model
 *  is shared, so an inactive panel costs nothing to leave unmounted and every
 *  chart in it costs a layout pass to keep. */
export function SubPanel({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <div role="tabpanel" id={`subpanel-${id}`} aria-labelledby={`subtab-${id}`} tabIndex={0}>
      {children}
    </div>
  )
}
